import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { payloadAPI } from '@/lib/payload'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { CodeUpload } from '@/components/developer/code-upload'
import { AlgorithmPreview } from '@/components/developer/algorithm-preview'
import { toast } from 'sonner'
import {
  Upload,
  FileText,
  Code,
  Image,
  Video,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react'

export const Route = createFileRoute('/developer/submit')({
  component: SubmitAlgorithmComponent,
})

type FormData = {
  name: string
  slug: string
  description: string
  fullDescription: string
  category: string
  tags: string[]
  pricingType: 'one-time' | 'subscription' | 'free' | 'custom'
  price?: number
  subscriptionPrice?: {
    monthly: number
    yearly: number
  }
  timeframe: string
  supportedAssets: string[]
  riskPerTrade: number
  maxPositions: number
  minCapital: number
  platform: string
  propFirmCompatible: boolean
  supportedPropFirms: Array<{
    firm: string
    verified: boolean
  }>
  performanceMetrics: {
    totalReturn?: number
    winRate?: number
    profitFactor?: number
    maxDrawdown?: number
    sharpeRatio?: number
    sortinoRatio?: number
    calmarRatio?: number
  }
  tradingParameters: {
    timeframe: string
    supportedAssets: string[]
    riskPerTrade: number
    maxPositions: number
  }
  requirements: {
    minCapital: number
    platform: string
    propFirmCompatible: boolean
    supportedPropFirms: Array<{
      firm: string
      verified: boolean
    }>
  }
}

function SubmitAlgorithmComponent() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [uploadedFiles, setUploadedFiles] = useState<Array<{
    name: string
    type: string
    file: File
    url?: string
  }>>([])
  const [uploadedImages, setUploadedImages] = useState<Array<{
    caption: string
    file: File
    url?: string
  }>>([])

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid }
  } = useForm<FormData>({
    mode: 'onChange'
  })

  const watchedValues = watch()

  const submitMutation = useMutation({
    mutationFn: (data: FormData) => payloadAPI.submitAlgorithm(data),
    onSuccess: () => {
      toast.success('Algorithm submitted successfully! It will be reviewed by our team.')
      navigate({ to: '/developer/dashboard' })
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to submit algorithm')
    }
  })

  const onSubmit = (data: FormData) => {
    const submissionData = {
      ...data,
      files: uploadedFiles.map(file => ({
        name: file.name,
        type: file.type,
        file: file.url || file.file
      })),
      images: uploadedImages.map(img => ({
        caption: img.caption,
        image: img.url || img.file
      })),
      status: 'pending'
    }

    submitMutation.mutate(submissionData)
  }

  const steps = [
    { id: 1, title: 'Basic Information', description: 'Algorithm details and description' },
    { id: 2, title: 'Code & Files', description: 'Upload your algorithm code and documentation' },
    { id: 3, title: 'Performance Metrics', description: 'Provide backtest results and performance data' },
    { id: 4, title: 'Requirements & Pricing', description: 'Platform requirements and pricing information' },
    { id: 5, title: 'Preview & Submit', description: 'Review your submission before sending' }
  ]

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return renderBasicInfo()
      case 2:
        return renderFilesStep()
      case 3:
        return renderPerformanceStep()
      case 4:
        return renderRequirementsStep()
      case 5:
        return renderPreviewStep()
      default:
        return null
    }
  }

  const renderBasicInfo = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Algorithm Name *
          </label>
          <input
            type="text"
            {...register('name', { required: true })}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="My Trading Algorithm"
          />
          {errors.name && (
            <p className="text-destructive text-sm mt-1">Algorithm name is required</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            URL Slug *
          </label>
          <input
            type="text"
            {...register('slug', { required: true })}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="my-trading-algorithm"
          />
          <p className="text-xs text-muted-foreground mt-1">
            URL-friendly version of the name
          </p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Brief Description *
        </label>
        <textarea
          {...register('description', { required: true })}
          rows={3}
          className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="A short description of your algorithm (max 200 characters)"
          maxLength={200}
        />
        {errors.description && (
          <p className="text-destructive text-sm mt-1">Description is required</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Full Description *
        </label>
        <textarea
          {...register('fullDescription', { required: true })}
          rows={6}
          className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Detailed explanation of your algorithm's strategy, methodology, and usage instructions"
        />
        {errors.fullDescription && (
          <p className="text-destructive text-sm mt-1">Full description is required</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Category *
          </label>
          <select
            {...register('category', { required: true })}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Select a category</option>
            <option value="trend-following">Trend Following</option>
            <option value="mean-reversion">Mean Reversion</option>
            <option value="arbitrage">Arbitrage</option>
            <option value="scalping">Scalping</option>
            <option value="swing-trading">Swing Trading</option>
            <option value="momentum">Momentum</option>
            <option value="breakout">Breakout</option>
            <option value="multi-timeframe">Multi-Timeframe</option>
          </select>
          {errors.category && (
            <p className="text-destructive text-sm mt-1">Category is required</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Tags
          </label>
          <input
            type="text"
            {...register('tags')}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="forex, scalping, momentum (comma separated)"
          />
        </div>
      </div>
    </div>
  )

  const renderFilesStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Algorithm Files</h3>
        <CodeUpload
          onFilesChange={setUploadedFiles}
          acceptedTypes={['.mq4', '.mq5', '.ex4', '.ex5', '.py', '.js', '.ts', '.txt', '.pdf']}
        />
        {uploadedFiles.length === 0 && (
          <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <div>
              <p className="text-sm font-medium text-yellow-800">Required Files</p>
              <p className="text-sm text-yellow-700">
                You must upload at least the source code or executable file of your algorithm.
              </p>
            </div>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Screenshots & Images</h3>
        <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => {
              const files = Array.from(e.target.files || [])
              const newImages = files.map(file => ({
                caption: '',
                file
              }))
              setUploadedImages(prev => [...prev, ...newImages])
            }}
            className="hidden"
            id="image-upload"
          />
          <label htmlFor="image-upload" className="cursor-pointer">
            <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium text-foreground mb-2">
              Upload Screenshots
            </p>
            <p className="text-sm text-muted-foreground">
              Add images showing your algorithm's performance, setup, or interface
            </p>
          </label>
        </div>

        {uploadedImages.length > 0 && (
          <div className="mt-4 space-y-3">
            {uploadedImages.map((img, index) => (
              <div key={index} className="flex items-center gap-4 p-3 bg-card border rounded-lg">
                <Image className="h-8 w-8 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Image caption (optional)"
                  value={img.caption}
                  onChange={(e) => {
                    const newImages = [...uploadedImages]
                    newImages[index].caption = e.target.value
                    setUploadedImages(newImages)
                  }}
                  className="flex-1 px-3 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setUploadedImages(prev => prev.filter((_, i) => i !== index))
                  }}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  const renderPerformanceStep = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800">Performance Requirements</p>
            <p className="text-sm text-blue-700">
              Provide accurate backtest results. All algorithms will be independently verified before publication.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Total Return (%)
          </label>
          <input
            type="number"
            step="0.01"
            {...register('performanceMetrics.totalReturn')}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="15.5"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Win Rate (%)
          </label>
          <input
            type="number"
            step="0.01"
            {...register('performanceMetrics.winRate')}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="65.5"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Profit Factor
          </label>
          <input
            type="number"
            step="0.01"
            {...register('performanceMetrics.profitFactor')}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="1.85"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Max Drawdown (%)
          </label>
          <input
            type="number"
            step="0.01"
            {...register('performanceMetrics.maxDrawdown')}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="12.3"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Sharpe Ratio
          </label>
          <input
            type="number"
            step="0.01"
            {...register('performanceMetrics.sharpeRatio')}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="1.45"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Sortino Ratio
          </label>
          <input
            type="number"
            step="0.01"
            {...register('performanceMetrics.sortinoRatio')}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="2.10"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Backtest Report (Optional)
        </label>
        <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-lg font-medium text-foreground mb-2">
            Upload Backtest Report
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            Upload a detailed backtest report or analysis
          </p>
          <input
            type="file"
            accept=".pdf,.txt,.csv,.xlsx"
            className="hidden"
            id="backtest-report"
          />
          <label htmlFor="backtest-report" className="cursor-pointer">
            <Button variant="outline">
              <Upload className="h-4 w-4 mr-2" />
              Choose File
            </Button>
          </label>
        </div>
      </div>
    </div>
  )

  const renderRequirementsStep = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Pricing Type *
          </label>
          <select
            {...register('pricingType', { required: true })}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="free">Free</option>
            <option value="one-time">One-time Purchase</option>
            <option value="subscription">Subscription</option>
            <option value="custom">Custom Pricing</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Platform *
          </label>
          <select
            {...register('requirements.platform', { required: true })}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Select platform</option>
            <option value="mt4">MetaTrader 4 (MT4)</option>
            <option value="mt5">MetaTrader 5 (MT5)</option>
            <option value="tradingview">TradingView</option>
            <option value="ctrader">cTrader</option>
            <option value="ninjatrader">NinjaTrader</option>
            <option value="python">Python</option>
            <option value="javascript">JavaScript/Node.js</option>
          </select>
        </div>
      </div>

      {(watchedValues.pricingType === 'one-time' || watchedValues.pricingType === 'subscription') && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {watchedValues.pricingType === 'one-time' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Price (USD) *
              </label>
              <input
                type="number"
                step="0.01"
                {...register('price', { required: watchedValues.pricingType === 'one-time' })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="299.00"
              />
            </div>
          )}

          {watchedValues.pricingType === 'subscription' && (
            <>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Monthly Price (USD) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register('subscriptionPrice.monthly', { required: true })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="49.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Yearly Price (USD) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register('subscriptionPrice.yearly', { required: true })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="499.00"
                />
              </div>
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Minimum Capital (USD) *
          </label>
          <input
            type="number"
            step="100"
            {...register('requirements.minCapital', { required: true })}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="1000"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Timeframe *
          </label>
          <select
            {...register('tradingParameters.timeframe', { required: true })}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Select timeframe</option>
            <option value="M1">M1 (1 minute)</option>
            <option value="M5">M5 (5 minutes)</option>
            <option value="M15">M15 (15 minutes)</option>
            <option value="M30">M30 (30 minutes)</option>
            <option value="H1">H1 (1 hour)</option>
            <option value="H4">H4 (4 hours)</option>
            <option value="D1">D1 (Daily)</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Risk Per Trade (%)
          </label>
          <input
            type="number"
            step="0.1"
            {...register('tradingParameters.riskPerTrade')}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="2.0"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Maximum Positions
          </label>
          <input
            type="number"
            {...register('tradingParameters.maxPositions')}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="5"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Supported Assets *
        </label>
        <div className="space-y-2">
          {['forex', 'stocks', 'crypto', 'commodities', 'indices'].map((asset) => (
            <label key={asset} className="flex items-center">
              <input
                type="checkbox"
                {...register('tradingParameters.supportedAssets')}
                value={asset}
                className="mr-2"
              />
              <span className="capitalize">{asset}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="flex items-center">
          <input
            type="checkbox"
            {...register('requirements.propFirmCompatible')}
            className="mr-2"
          />
          <span className="text-sm font-medium text-foreground">
            Compatible with Prop Firm Rules
          </span>
        </label>
        <p className="text-xs text-muted-foreground mt-1">
          Check if this algorithm complies with typical prop firm trading rules
        </p>
      </div>
    </div>
  )

  const renderPreviewStep = () => (
    <div>
      <h3 className="text-lg font-semibold mb-4">Preview Your Algorithm</h3>
      <AlgorithmPreview data={watchedValues} files={uploadedFiles} images={uploadedImages} />
    </div>
  )

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-4">
            Submit Your Algorithm
          </h1>
          <p className="text-lg text-muted-foreground">
            Share your trading algorithm with our community of traders
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= step.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {step.id}
                  </div>
                  <span className="text-xs mt-2 text-center max-w-24">
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-4 ${
                    currentStep > step.id ? 'bg-primary' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <div className="bg-card rounded-lg p-8 border">
            <h2 className="text-xl font-semibold mb-6">
              {steps.find(s => s.id === currentStep)?.title}
            </h2>
            <p className="text-muted-foreground mb-6">
              {steps.find(s => s.id === currentStep)?.description}
            </p>

            {renderStep()}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1}
            >
              Previous
            </Button>

            <div className="flex items-center gap-2">
              {currentStep < steps.length ? (
                <Button
                  type="button"
                  onClick={() => {
                    if (currentStep < steps.length) {
                      setCurrentStep(currentStep + 1)
                    }
                  }}
                  disabled={
                    (currentStep === 1 && (!watchedValues.name || !watchedValues.description)) ||
                    (currentStep === 2 && uploadedFiles.length === 0)
                  }
                >
                  Next Step
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={submitMutation.isPending || !isValid}
                >
                  {submitMutation.isPending ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Submit Algorithm
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}